import {
  type FetchProductListParams,
  fetchProducts,
  fetchSingleProduct,
  fetchTariffProducts
} from 'Services/ProductManagement/Features/Product/productSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

interface UseProductsProps {
  hasNoTariff?: boolean
  inTariff?: string
}

export const SELECTED_PRODUCT_PARAMETER_KEY = 'selectedProduct'

function useProductsWithParams({ hasNoTariff = false, inTariff }: UseProductsProps) {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    products,
    loading,
    tariffProducts,
    tariffProductsLoading,
    total_results,
    next_page,
    previous_page
  } = useTypedSelector(state => state.product)

  const [params, setParams] = useState<FetchProductListParams>({
    filter: searchParams.get('filter') ?? undefined,
    search_text: searchParams.get('search_text') ?? undefined,
    sort_by: searchParams.get('sort_by') ?? 'productCode',
    sort_order: searchParams.get('sort_order') ?? SORT_ORDER_ASC,
    start_index: searchParams.get('start_index') ?? undefined,
    count: ROWS_PER_PAGE_DEFAULT,
    has_no_tariff: hasNoTariff,
    in_tariff: inTariff
  })

  const selectedProductQueryParam = searchParams.get(SELECTED_PRODUCT_PARAMETER_KEY) ?? undefined
  const [selectedProductId, setSelectedProductId] = useState(selectedProductQueryParam)

  const setQueryParams = () => {
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value.toString())
      } else {
        searchParams.delete(key)
      }
    })

    // Set selectedProductId if it exists
    selectedProductId && searchParams.set(SELECTED_PRODUCT_PARAMETER_KEY, selectedProductId)

    setSearchParams(searchParams)
  }

  useEffect(() => {
    if (params.in_tariff) {
      dispatch(fetchTariffProducts(params))
    } else {
      dispatch(fetchProducts(params))
    }
    setQueryParams()
  }, [params])

  useEffect(() => {
    if (selectedProductId) {
      dispatch(fetchSingleProduct({ id: selectedProductId }))
      setQueryParams()
    } else {
      searchParams.delete(SELECTED_PRODUCT_PARAMETER_KEY)
      setSearchParams(searchParams)
    }
  }, [selectedProductId])

  const fetchTariffProductsWithParams = (productParams: FetchProductListParams) => {
    dispatch(fetchTariffProducts(productParams))
    setQueryParams()
  }

  const fetchTariffProductsWithParamsAfterDeleteProduct = (
    productParams: FetchProductListParams
  ) => {
    dispatch(fetchTariffProducts(productParams))
    setQueryParams()
    searchParams.delete(SELECTED_PRODUCT_PARAMETER_KEY)
    setSearchParams(searchParams)
  }

  const fetchProductsWithParams = () => {
    dispatch(fetchProducts(params))
    setQueryParams()
  }

  return {
    products,
    loading,
    tariffProducts,
    tariffProductsLoading,
    params,
    setParams,
    total_results,
    next_page,
    previous_page,
    selectedProductId,
    setSelectedProductId,
    fetchTariffProductsWithParams,
    fetchProductsWithParams,
    fetchTariffProductsWithParamsAfterDeleteProduct
  }
}

export default useProductsWithParams
